import * as d3 from 'd3';
import draghandle, { HORIZONTAL } from './d3-draghandle';
import chartTooltip from './d3-tooltip';
import { colors, LEGEND_ITEM_RADIUS, getSVGPosition } from '../utils';
import './d3-sensitivitychart.css';

const color = {
  red: 'rgb(214, 39, 40)',
  blue: 'rgb(31, 119, 180)',
};

export default function () {
  const margin = { top: 30, right: 20, bottom: 60, left: 60 };

  let width = 400;
  let height = 300;
  let svg;
  let _data;
  let threshold;
  let tolerance = 0.1;
  let triggersEnabled;
  let units = 'percentage';
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let selected;
  let legendLabels;
  let xLabel;
  let yLabel;
  let xAxisFormatter = d3.format('+.2%');
  let yAxisFormatter = d3.format('0.2f');
  let yExtent = [0, 1];
  let handle;
  let tooltip;
  let trigger;

  const dispatch = d3.dispatch('selected');

  function chart(selection) {
    selection.each(function (data) {
      _data = Object.values(data.data);
      yExtent = data.extent;
      chartWidth = width - margin.left - margin.right;
      chartHeight = height - margin.top - margin.bottom;

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();

        g.append('g')
          .attr('class', 'sensitivity-chart__tolerance');

        g.append('g')
          .attr('class', 'sensitivity-chart__area');

        g.append('g')
          .attr('class', 'sensitivity-chart__curve');

        createLabels();
        createHandle();
        createTrigger();
        createTolerance();
        createLegend();
        createTooltip();
      }

      svg.attr('width', width).attr('height', height);

      drawAxes();
      drawArea();
      drawCurve();
      drawLabels();
      drawHandle();
      drawTrigger();
      drawTolerance();
      drawLegend();
    });
  }

  function createScales() {
    x = d3.scaleLinear().rangeRound([0, chartWidth]);
    y = d3.scaleLinear().rangeRound([chartHeight, 0]);

    x.domain(d3.extent(_data[0].map(function (d) { return d.x; })));

    switch (units) {
      case 'percentage':
        y.domain([0, 1]);
        break;
      case 'ratio':
      default:
        y.domain([yExtent[0] > 0 ? 0 : yExtent[0], yExtent[1] < 0 ? 0 : yExtent[1]]).nice();
        break;
    }
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'sensitivity-chart__axis sensitivity-chart__axis--y');

    const xAxis = svg.select('g')
      .append('g')
      .attr('class', 'sensitivity-chart__axis sensitivity-chart__axis--x');

    xAxis.append('line')
      .attr('class', 'baseline-halo');

    xAxis.append('line')
      .attr('class', 'baseline');
  }

  function createLabels() {
    svg.select('g')
      .append('text')
      .attr('class', 'sensitivity-chart__label sensitivity-chart__label--x')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text(xLabel);

    svg.select('g')
      .append('text')
      .attr('class', 'sensitivity-chart__label sensitivity-chart__label--y')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text(yLabel);
  }

  function createHandle() {
    handle = draghandle().animate(false)
      .height(chartHeight)
      .extent(x.range())
      .on('dragged', function dragged(d) {
        const value = x.invert(d);
        dispatch.call('selected', chart, selected.key, value);
      })
      .on('dragended', function dragended(d) {
        const value = x.invert(d);
        dispatch.call('selected', chart, selected.key, value);
      });

    const handleContainer = svg.select('g');
    handleContainer.datum(x(selected.value)).call(handle);
  }

  function createTrigger() {
    trigger = draghandle().animate(false)
      .orientation(HORIZONTAL)
      .height(chartWidth)
      .extent(y.range())
      .on('dragged', function dragged(d) {
        const value = y.invert(d);
        dispatch.call('selected', chart, 'threshold', value);
      })
      .on('dragended', function dragended(d) {
        const value = y.invert(d);
        dispatch.call('selected', chart, 'threshold', value);
      });

    const handleContainer = svg.select('g');
    handleContainer.datum(y(threshold)).call(trigger);
  }

  function createTolerance() {
    const tol = svg.select('.sensitivity-chart__tolerance');

    tol.append('line')
      .attr('class', 'center')
      .attr('x1', 0)
      .attr('opacity', 0.1)
      .attr('stroke', 'blue')
      .attr('stroke-width', '1px')
      .attr('stroke-dasharray', '5, 5');

    tol.append('line')
      .attr('class', 'upper')
      .attr('x1', 0)
      .attr('opacity', 0.1)
      .attr('stroke', 'blue')
      .attr('stroke-width', '1px')
      .attr('stroke-dasharray', '5, 5');

    tol.append('line')
      .attr('class', 'lower')
      .attr('x1', 0)
      .attr('opacity', 0.1)
      .attr('stroke', 'blue')
      .attr('stroke-width', '1px')
      .attr('stroke-dasharray', '5, 5');

    tol.append('rect')
      .attr('class', 'shaded')
      .attr('x', 0)
      .attr('opacity', 0.1)
      .attr('fill', 'blue');
  }

  function createLegend() {
    svg.select('g')
      .append('g')
      .attr('class', 'sensitivity-chart__legend')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .attr('text-anchor', 'end');
  }

  function createTooltip() {
    tooltip = chartTooltip();
    const tooltipContainer = d3.select('body')
      .append('div')
      .attr('class', 'sensitivity-chart__tooltip');

    tooltipContainer.datum([]).call(tooltip);
  }

  function drawAxes() {
    const xAxis = svg.select('.sensitivity-chart__axis--x');

    xAxis.attr('transform', 'translate(0,' + chartHeight + ')')
      .call(d3.axisBottom(x).tickFormat(xAxisFormatter).tickSize(-chartHeight))
      .select('.domain').remove();

    xAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    xAxis.selectAll('text')
      .attr('dx', '-12px')
      .attr('dy', '12px')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end');

    const baselineYOffset = 3;

    xAxis.select('.baseline-halo')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', baselineYOffset)
      .attr('y2', baselineYOffset)
      .attr('stroke', '#bbb')
      .attr('stroke-width', 6)
      .attr('stroke-linecap', 'round');

    xAxis.select('.baseline')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', baselineYOffset)
      .attr('y2', baselineYOffset)
      .attr('stroke', '#eee')
      .attr('stroke-width', 4)
      .attr('stroke-linecap', 'round');

    const yAxis = svg.select('.sensitivity-chart__axis--y')
      .call(d3.axisLeft(y).tickFormat(yAxisFormatter).tickSize(-chartWidth));

    yAxis.select('.domain').remove();

    yAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    yAxis.selectAll('.tick text')
      .attr('x', -6)
      .attr('dy', 4);
  }

  function drawCurve() {
    const line = d3.line()
      .curve(d3.curveLinear)
      .x(function (d) { return x(d.x); })
      .y(function (d) { return y(d.sensitivity); });

    const curve = svg.select('.sensitivity-chart__curve')
      .selectAll('path')
      .data(_data);

    curve.enter()
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', function (d, i) { return selected.key === 'vol' ? (i === 0 ? color.red : 'pink') : (i === 0 ? color.blue : 'lightblue'); })
      .attr('stroke-width', 2)
      .merge(curve)
      .attr('d', line);

    curve.exit()
      .remove();
  }

  function calculateZero(x1, y1, x2, y2) {
    return x1 - y1 * (x2 - x1) / (y2 - y1);
  }

  // TODO: Make this none-awful!
  function calculateEnterExit(_data, threshold, tolerance) {
    const upper = _data.map(function (x) { return Math.sign(x.sensitivity - threshold - tolerance); });
    const lower = _data.map(function (x) { return Math.sign(x.sensitivity - threshold + tolerance); });

    let upperDiff = [];
    let lowerDiff = [];

    for (let i = 0; i < upper.length - 1; i++) {
      upperDiff.push(upper[i + 1] - upper[i]);
      lowerDiff.push(lower[i + 1] - lower[i])
    }

    let enter = [];
    let exit = [];

    for (let i = 0; i < upperDiff.length; i++) {
      if (upperDiff[i] < 0) {
        enter.push({ x: calculateZero(_data[i].x, _data[i].sensitivity - threshold - tolerance, _data[i + 1].x, _data[i + 1].sensitivity - threshold - tolerance), y: threshold + tolerance, index: i });
      }

      if (lowerDiff[i] > 0) {
        enter.push({ x: calculateZero(_data[i].x, _data[i].sensitivity - threshold + tolerance, _data[i + 1].x, _data[i + 1].sensitivity - threshold + tolerance), y: threshold - tolerance, index: i });
      }

      if (upperDiff[i] > 0) {
        exit.push({ x: calculateZero(_data[i].x, _data[i].sensitivity - threshold - tolerance, _data[i + 1].x, _data[i + 1].sensitivity - threshold - tolerance), y: threshold + tolerance, index: i });
      }

      if (lowerDiff[i] < 0) {
        exit.push({ x: calculateZero(_data[i].x, _data[i].sensitivity - threshold + tolerance, _data[i + 1].x, _data[i + 1].sensitivity - threshold + tolerance), y: threshold - tolerance, index: i });
      }
    }

    // What if you never cross over?
    if (enter.length === 0 && exit.length === 0 && upper[0] < 0 && lower[0] > 0) {
      enter.unshift({ x: _data[0].x, y: _data[0].sensitivity, index: 0 });
      exit.push({ x: _data[_data.length - 1].x, y: _data[_data.length - 1].sensitivity, index: _data.length - 1 });
    }

    // What if you exit first? Insert dummy enter at start
    if ((exit[0] !== undefined && enter[0] === undefined) || ((enter[0] !== undefined && exit[0] !== undefined) && exit[0].x < enter[0].x)) {
      enter.unshift({ x: _data[0].x, y: _data[0].sensitivity, index: 0 });
    }

    // What if you enter last? Insert dummy exit at end
    if ((enter[0] !== undefined && exit[0] === undefined) || ((enter[enter.length - 1] !== undefined && exit[exit.length - 1] !== undefined) && enter[enter.length - 1].x > exit[exit.length - 1].x)) {
      exit.push({ x: _data[_data.length - 1].x, y: _data[_data.length - 1].sensitivity, index: _data.length - 1 });
    }

    const areas = [];

    for (let i = 0; i < enter.length; i++) {
      const points = [];
      let j = enter[i].index;

      points.push(enter[i]);

      while (j <= exit[i].index) {
        points.push({ x: _data[j].x, y: _data[j].sensitivity });
        j++;
      }

      points.push(exit[i]);

      areas.push(points);
    }

    return areas;
  }

  function drawArea() {
    let areas = [];

    if (threshold !== null && triggersEnabled) {
      areas = calculateEnterExit(_data[0], threshold, tolerance);
    }

    const area = d3.area()
      .curve(d3.curveLinear)
      .x(function (d) { return x(d.x); })
      .y1(function (d) { return y(d.y); })
      .y0(y(threshold));

    const fill = svg.select('.sensitivity-chart__area')
      .selectAll('path')
      .data(areas);

    fill.enter()
      .append('path')
      .attr('fill', selected.key === 'vol' ? color.red : color.blue)
      .attr('opacity', 0.5)
      .on('mouseover', function () {
        tooltip.show();
      })
      .on('mousemove', function (d) {
        const pos = d3.mouse(this);
        const SVGPos = getSVGPosition(this);

        tooltip.update(
          { x: pos[0] + SVGPos[0], y: pos[1] + SVGPos[1] },
          [{ key: 'start', value: d3.format('+.2%')(d[0].x) }, { key: 'end', value: d3.format('+.2%')(d[d.length - 1].x) }],
          'Within ' + d3.format('.0%')(tolerance)
        );
      })
      .on('mouseout', function () {
        tooltip.hide();
      })
      .merge(fill)
      .attr('d', area);

    fill.exit()
      .remove();
  }

  function drawLabels() {
    svg.select('.sensitivity-chart__label--x')
      .attr('x', chartWidth / 2)
      .attr('y', chartHeight + margin.top + 17);

    svg.select('.sensitivity-chart__label--y')
      .attr('transform', 'translate(-60, ' + chartHeight / 2 + ')rotate(-90)')
      .text(yLabel);
  }

  function drawHandle() {
    handle.height(chartHeight)
      .extent(x.range());

    const handleContainer = svg.select('g');
    handleContainer.datum(x(selected.value)).call(handle);
  }

  function drawTrigger() {
    trigger.height(chartWidth)
      .extent(y.range())
      .visible(false);

    const handleContainer = svg.select('g');
    handleContainer.datum(y(threshold)).call(trigger);
  }

  function drawTolerance() {
    const tol = svg.select('.sensitivity-chart__tolerance');

    const upperThresold = threshold + tolerance;
    const lowerThreshold = threshold - tolerance;

    tol.select('.center')
      .attr('x2', chartWidth)
      .attr('y1', y(threshold))
      .attr('y2', y(threshold));

    tol.select('.upper')
      .attr('x2', chartWidth)
      .attr('y1', y(upperThresold))
      .attr('y2', y(upperThresold));

    tol.select('.lower')
      .attr('x2', chartWidth)
      .attr('y1', y(lowerThreshold))
      .attr('y2', y(lowerThreshold));

    tol.select('.shaded')
      .attr('y', y(upperThresold))
      .attr('width', chartWidth)
      .attr('height', Math.abs(y(tolerance * 2) - y(0)));

    tol.style('display', (threshold === null || !triggersEnabled) ? 'none' : 'block');
    tol.select('.center', 'block');
  }

  function drawLegend() {
    const data = legendLabels;

    const legend = svg.select('.sensitivity-chart__legend')
      .selectAll('g')
      .data(data);

    legend.exit().remove();

    const legendEnter = legend.enter().append('g');

    const SPACE_BETWEEN_LEGEND_ITEMS = 50;

    legend.merge(legendEnter)
      .attr('transform', function (d, i) { return 'translate(' + (-i * SPACE_BETWEEN_LEGEND_ITEMS + chartWidth + 4) + ',' + -24 + ')'; });

    legendEnter.append('circle')
      .attr('cy', 9)
      .attr('r', LEGEND_ITEM_RADIUS)
      .attr('stroke', 'black')
      .attr('stroke-width', 1);

    legendEnter.append('text')
      .attr('y', 9.5)
      .attr('dy', '0.32em')
      .attr('fill', 'black');

    legend.merge(legendEnter)
      .select('circle')
      .attr('cx', -10)
      .attr('fill', function (d, i) { return selected.key === 'vol' ? (i === 0 ? color.red : 'pink') : (i === 0 ? color.blue : 'lightblue'); })

    legend.merge(legendEnter)
      .select('text')
      .attr('x', -21)
      .text(function (d) { return d; });
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.selected = function (value) {
    if (!arguments.length) return selected;
    selected = value;
    return chart;
  }

  chart.threshold = function (value) {
    if (!arguments.length) return threshold;
    threshold = value;
    return chart;
  }

  chart.triggersEnabled = function (value) {
    if (!arguments.length) return triggersEnabled;
    triggersEnabled = value;
    return chart;
  }

  chart.units = function (value) {
    if (!arguments.length) return units;
    units = value;
    return chart;
  }

  chart.legendLabels = function (value) {
    if (!arguments.length) return legendLabels;
    legendLabels = value;
    return chart;
  }

  chart.xLabel = function (value) {
    if (!arguments.length) return xLabel;
    xLabel = value;
    return chart;
  }

  chart.yLabel = function (value) {
    if (!arguments.length) return yLabel;
    yLabel = value;
    return chart;
  }

  chart.xAxisFormatter = function (value) {
    if (!arguments.length) return xAxisFormatter;
    xAxisFormatter = value;
    return chart;
  }

  chart.yAxisFormatter = function (value) {
    if (!arguments.length) return yAxisFormatter;
    yAxisFormatter = value;
    return chart;
  }

  chart.tolerance = function (value) {
    if (!arguments.length) return tolerance;
    tolerance = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  chart.cleanup = function () {
    d3.select('.sensitivity-chart__tooltip').remove();
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-sensitivitychart.js